import type { UIMessage } from "@ai-sdk/react";
import { useMemo, useRef } from "react";

export interface StableSettledAndStreamingMessages {
  /** Prior turns; array reference is reused across stream chunks when contents are unchanged. */
  settledMessages: UIMessage[];
  streamingAssistant: UIMessage | null;
}

export interface UseStableSettledAndStreamingMessagesParams {
  messages: UIMessage[];
  isStreaming: boolean;
  showPendingThinking: boolean;
}

/**
 * Splits chat messages into settled history and an in-flight assistant;
 * settled array reference is reused until a prior turn changes.
 */
export const useStableSettledAndStreamingMessages = ({
  messages,
  isStreaming,
  showPendingThinking,
}: UseStableSettledAndStreamingMessagesParams): StableSettledAndStreamingMessages => {
  const settledRef = useRef<UIMessage[]>(messages);

  return useMemo(() => {
    const lastMessage = messages[messages.length - 1];
    const streamingAssistantId =
      isStreaming && !showPendingThinking && lastMessage?.role === "assistant"
        ? lastMessage.id
        : null;

    if (streamingAssistantId == null) {
      settledRef.current = messages;
      return { settledMessages: messages, streamingAssistant: null };
    }

    const settled: UIMessage[] = [];
    let streamingAssistant: UIMessage | null = null;

    for (const message of messages) {
      if (message.role === "assistant" && message.id === streamingAssistantId) {
        streamingAssistant = message;
      } else {
        settled.push(message);
      }
    }

    const prevSettled = settledRef.current;
    const settledUnchanged =
      prevSettled.length === settled.length &&
      prevSettled.every((message, index) => message === settled[index]);

    if (settledUnchanged) {
      return { settledMessages: prevSettled, streamingAssistant };
    }

    settledRef.current = settled;
    return { settledMessages: settled, streamingAssistant };
  }, [messages, isStreaming, showPendingThinking]);
};
